/**
 * PerspectivePanel - Panel individual de una perspectiva rabínica
 * Muestra categorías, texto original y notas
 */

import React from 'react';
import { Trash2, ScrollText, StickyNote } from 'lucide-react';
import CategoryBadges from './CategoryBadges';

const PerspectivePanel = ({ perspectiveKey, perspectiveConfig, mitzvah, onRemove }) => {
  // Obtener datos de la perspectiva
  const dataMap = {
    rambam: mitzvah.rambamPerspectives,
    ramban: mitzvah.rambanPerspectives,
    chinuch: mitzvah.chinuchPerspectives,
    semag: mitzvah.semagPerspectives,
    bahag: mitzvah.bahagPerspectives,
    hebrewRoots: mitzvah.hebrewRootsPerspectives
  };

  const perspectiveData = dataMap[perspectiveKey];

  if (!perspectiveData || perspectiveData.length === 0) {
    return (
      <div className={`
        bg-white border-2 rounded-lg p-6
        ${perspectiveConfig.colorClasses.border}
        ${perspectiveConfig.colorClasses.bg}
      `}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">
            {perspectiveConfig.fullName}
          </h3>
          <button
            onClick={onRemove}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Remover del tablero"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 italic">
          No hay datos disponibles para esta perspectiva
        </p>
      </div>
    );
  }

  return (
    <div className={`
      bg-white border-2 rounded-lg overflow-hidden
      ${perspectiveConfig.colorClasses.border}
      shadow-md hover:shadow-lg transition-shadow
    `}>
      {/* Header */}
      <div className={`
        ${perspectiveConfig.colorClasses.bg}
        border-b-2 ${perspectiveConfig.colorClasses.border}
        px-6 py-4
      `}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {perspectiveConfig.fullName}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {perspectiveConfig.name}
            </p>
          </div>
          <button
            onClick={onRemove}
            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
            title="Remover del tablero"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Contenido */}
      <div className="p-6 space-y-6">
        {perspectiveData.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">
              No hay datos disponibles para esta perspectiva en este mandamiento.
            </p>
          </div>
        ) : perspectiveData.map((perspective, index) => (
          <div key={perspective.id || index} className="space-y-4">
            {/* Categorías visuales */}
            <CategoryBadges
              perspectiveKey={perspectiveKey}
              perspective={perspective}
            />

            {/* Versículo fuente */}
            {perspective.sourceVerse && (
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4">
                <p className="text-xs font-semibold text-blue-900 mb-2 uppercase tracking-wide">
                  📖 Versículo fuente
                </p>
                <p className="text-sm font-medium text-blue-800">
                  {perspective.sourceVerse.book?.bookNameEs} {perspective.sourceVerse.chapter}:{perspective.sourceVerse.verse}
                </p>
                {perspective.sourceVerse.verseText && (
                  <p className="text-sm text-gray-700 mt-2 italic leading-relaxed">
                    "{perspective.sourceVerse.verseText}"
                  </p>
                )}
              </div>
            )}

            {/* Texto original */}
            {perspective.originalText && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
                  <ScrollText className="w-4 h-4" />
                  Texto Original
                </p>
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {perspective.originalText}
                  </p>
                </div>
              </div>
            )}

            {/* Traducción al español */}
            {perspective.originalTextEs && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Traducción al Español
                </p>
                <div className="bg-white border-2 border-gray-300 rounded-lg p-4">
                  <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                    {perspective.originalTextEs}
                  </p>
                </div>
              </div>
            )}

            {/* Campos específicos de Rambam */}
            {perspectiveKey === 'rambam' && perspective.madahTitleEs && (
              <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg p-4">
                <p className="text-xs font-semibold text-indigo-900 mb-1 uppercase tracking-wide">
                  Sección Madah
                </p>
                <p className="text-sm text-indigo-800 font-medium">
                  {perspective.madahTitleEs}
                </p>
              </div>
            )}

            {/* Campos específicos de Chinuch */}
            {perspectiveKey === 'chinuch' && (
              <>
                {perspective.mitzvahTitleEs && (
                  <div className="bg-purple-50 border-l-4 border-purple-500 rounded-r-lg p-4">
                    <p className="text-xs font-semibold text-purple-900 mb-1 uppercase tracking-wide">
                      Título
                    </p>
                    <p className="text-sm text-purple-800 font-medium">
                      {perspective.mitzvahTitleEs}
                    </p>
                  </div>
                )}
                {perspective.shoreshEs && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Shoresh (Raíz)
                    </p>
                    <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                      <p className="text-sm text-gray-800 leading-relaxed">
                        {perspective.shoreshEs}
                      </p>
                    </div>
                  </div>
                )}
                {perspective.dinimEs && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Dinim (Leyes)
                    </p>
                    <div className="bg-teal-50 border border-teal-300 rounded-lg p-4">
                      <p className="text-sm text-gray-800 leading-relaxed">
                        {perspective.dinimEs}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Campos específicos de SEMAG */}
            {perspectiveKey === 'semag' && (
              <>
                {perspective.semagTitle && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
                      <ScrollText className="w-4 h-4" />
                      Texto Original (Hebreo)
                    </p>
                    <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                        {perspective.semagTitle}
                      </p>
                    </div>
                  </div>
                )}
                {perspective.semagTitleEs && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Traducción al Español
                    </p>
                    <div className="bg-white border-2 border-green-300 rounded-lg p-4">
                      <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                        {perspective.semagTitleEs}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Campos específicos de BAHAG */}
            {perspectiveKey === 'bahag' && (
              <>
                {perspective.mitzvahTitle && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
                      <ScrollText className="w-4 h-4" />
                      Texto Original (Hebreo)
                    </p>
                    <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                        {perspective.mitzvahTitle}
                      </p>
                    </div>
                  </div>
                )}
                {perspective.mitzvahTitleEs && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Traducción al Español
                    </p>
                    <div className="bg-white border-2 border-orange-300 rounded-lg p-4">
                      <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                        {perspective.mitzvahTitleEs}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Campos específicos de Hebrew Roots */}
            {perspectiveKey === 'hebrewRoots' && (
              <>
                {/* Versículo adicional (sourceVerse2) */}
                {perspective.sourceVerse2 && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4">
                    <p className="text-xs font-semibold text-blue-900 mb-2 uppercase tracking-wide">
                      📖 Versículo adicional
                    </p>
                    <p className="text-sm font-medium text-blue-800">
                      {perspective.sourceVerse2.book?.bookNameEs} {perspective.sourceVerse2.chapter}:{perspective.sourceVerse2.verse}
                    </p>
                    {perspective.sourceVerse2.verseText && (
                      <p className="text-sm text-gray-700 mt-2 italic leading-relaxed">
                        "{perspective.sourceVerse2.verseText}"
                      </p>
                    )}
                  </div>
                )}

                {perspective.mandamiento && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
                      <ScrollText className="w-4 h-4" />
                      Mandamiento
                    </p>
                    <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
                      <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                        {perspective.mandamiento}
                      </p>
                    </div>
                  </div>
                )}
                {perspective.descripcion && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Descripción
                    </p>
                    <div className="bg-white border-2 border-amber-300 rounded-lg p-4">
                      <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                        {perspective.descripcion}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Aplicación (Hebrew Roots) */}
                {perspective.aplicacion && (
                  <div className="bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4">
                    <p className="text-xs font-semibold text-green-900 mb-1 uppercase tracking-wide">
                      ✅ Aplicación
                    </p>
                    <p className="text-sm text-green-800 font-medium">
                      {perspective.aplicacion.name}
                    </p>
                    {perspective.aplicacion.description && (
                      <p className="text-xs text-green-700 mt-1">{perspective.aplicacion.description}</p>
                    )}
                    {perspective.aplicacion.validForToday !== undefined && (
                      <span className={`inline-block mt-2 px-2 py-1 text-xs rounded ${
                        perspective.aplicacion.validForToday 
                          ? 'bg-green-100 text-green-800 border border-green-300' 
                          : 'bg-gray-100 text-gray-800 border border-gray-300'
                      }`}>
                        {perspective.aplicacion.validForToday ? '✓ Válido hoy' : '✗ No válido hoy'}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Categorías (Hebrew Roots) */}
                {perspective.categorias && perspective.categorias.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Categorías ({perspective.categorias.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {perspective.categorias.map((cat, catIndex) => (
                        <div
                          key={cat.id || catIndex}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 border-2 border-purple-300 rounded-full text-sm font-medium"
                        >
                          {cat.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Tipo de Mandamiento (Hebrew Roots) */}
                {perspective.tipoActividad && (
                  <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg p-4">
                    <p className="text-xs font-semibold text-indigo-900 mb-1 uppercase tracking-wide">
                      📋 Tipo de Mandamiento
                    </p>
                    <p className="text-sm text-indigo-800 font-medium">
                      {perspective.tipoActividad.name}
                    </p>
                    {perspective.tipoActividad.description && (
                      <p className="text-xs text-indigo-700 mt-1">{perspective.tipoActividad.description}</p>
                    )}
                  </div>
                )}
                
                {/* Parashas (Hebrew Roots) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {perspective.parashaAnual && (
                    <div className="bg-teal-50 border-l-4 border-teal-500 rounded-r-lg p-4">
                      <p className="text-xs font-semibold text-teal-900 mb-1 uppercase tracking-wide">
                        📅 Parashá Anual
                      </p>
                      <p className="text-sm text-teal-800 font-medium">
                        {perspective.parashaAnual.parashaName}
                      </p>
                      <p className="text-xs text-teal-700 mt-1">
                        Ciclo: {perspective.parashaAnual.cycle || 'Anual'} | Orden: {perspective.parashaAnual.parashaOrder}
                      </p>
                    </div>
                  )}
                  
                  {perspective.parashaTrianual && (
                    <div className="bg-cyan-50 border-l-4 border-cyan-500 rounded-r-lg p-4">
                      <p className="text-xs font-semibold text-cyan-900 mb-1 uppercase tracking-wide">
                        📅 Parashá Trianual
                      </p>
                      <p className="text-sm text-cyan-800 font-medium">
                        {perspective.parashaTrianual.parashaName}
                      </p>
                      <p className="text-xs text-cyan-700 mt-1">
                        Ciclo: {perspective.parashaTrianual.cycle || 'Trianual'} | Orden: {perspective.parashaTrianual.parashaOrder}
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Notas */}
            {perspective.notes && perspective.notes.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
                  <StickyNote className="w-4 h-4" />
                  Notas Adicionales ({perspective.notes.length})
                </p>
                <div className="space-y-2">
                  {perspective.notes.map((note, noteIndex) => (
                    <div
                      key={note.id || noteIndex}
                      className="bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg p-4"
                    >
                      {note.noteText && (
                        <p className="text-sm text-gray-800 mb-2">{note.noteText}</p>
                      )}
                      {note.noteTextEs && (
                        <p className="text-sm text-gray-700 italic">{note.noteTextEs}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Separador entre múltiples entradas */}
            {index < perspectiveData.length - 1 && (
              <div className="border-t-2 border-gray-200 pt-4 mt-6"></div>
            )}
          </div>
        ))
        }
      </div>
    </div>
  );
};

export default PerspectivePanel;

